import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";
import type {
  Course,
  CourseChangeLog,
  CourseFilter,
  CourseReadiness,
  CreateCourseData,
  ModerationStats,
  UpdateAdmissionData,
  UpdateCourseData,
} from "./types";

export * from "./types";

/** Kurslar API — backend'dagi `CoursesController` ning ko'zgusi. */
export class CoursesApi extends BaseApi {
  private readonly baseUrl: string = "/courses";

  /**
   * Kurslar katalogi. Oddiy foydalanuvchi faqat PUBLISHED kurslarni oladi;
   * admin esa hamma holatni ko'radi (xohlasa `status` bilan toraytiradi).
   */
  getAll(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(this.baseUrl, { params: filter });
  }

  /** Joriy o'qituvchining o'z kurslari (holatidan qat'i nazar). */
  getMy(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(`${this.baseUrl}/my`, { params: filter });
  }

  getById(id: number) {
    return this.request.get<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}`);
  }

  /** RECORDED kurs yaratadi — LIVE kurslar `LiveCoursesApi` da. */
  create(data: CreateCourseData) {
    return this.request.post<Course, ApiResponse<Course>>(this.baseUrl, data);
  }

  update(id: number, data: UpdateCourseData) {
    return this.request.patch<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}`, data);
  }

  remove(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }

  /** Qoralama yoki rad etilgan kursni moderatsiyaga yuboradi. */
  submit(id: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/submit`);
  }

  /**
   * Nashrga tayyorlik ro'yxati — yuborish endpoint'i talab qiladigan
   * qoidalarning o'zi, shuning uchun sehrgarning REVIEW qadami bilan API
   * doim bir xil javob beradi.
   */
  getReadiness(id: number) {
    return this.request.get<CourseReadiness, ApiResponse<CourseReadiness>>(
      `${this.baseUrl}/${id}/readiness`,
    );
  }

  // ─── Moderatsiya (moderator / admin) ───────────────────────────────────────

  /** Moderatsiya paneli uchun umumiy sonlar. */
  getModerationStats() {
    return this.request.get<ModerationStats, ApiResponse<ModerationStats>>(
      `${this.baseUrl}/moderation/stats`,
    );
  }

  /** Moderatsiya ro'yxati: kutayotgan navbat (standart) yoki nashr/rad tarixi. */
  getModerationList(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(`${this.baseUrl}/moderation/list`, {
      params: filter,
    });
  }

  /** Navbatdagi kursni tasdiqlaydi va nashr qiladi. */
  approve(id: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/approve`);
  }

  /** Navbatdagi kursni sabab bilan rad etadi (sabab o'qituvchiga ko'rinadi). */
  reject(id: number, reason: string) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/reject`, {
      reason,
    });
  }

  /** Kursni bosh sahifa qatoriga qadaydi yoki undan oladi (moderator / admin). */
  setFeatured(id: number, featured: boolean) {
    return this.request.patch<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/feature`, {
      featured,
    });
  }

  /**
   * Nashr etilgan kursni arxivlaydi — u katalogdan chiqadi, lekin yozilgan
   * o'quvchilar kirish huquqini saqlab qoladi. "O'chirib bo'lmadi, 12 ta
   * o'quvchi bor" degan holatning javobi shu.
   */
  archive(id: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/archive`);
  }

  /**
   * Qabulni to'xtatish/davom ettirish va oxirgi kun (muallif yoki admin).
   * Kurs katalogda qoladi, o'qish davom etadi — faqat yangi yozilish va
   * to'lov yopiladi. Javobda qayta hisoblangan `admission`.
   */
  updateAdmission(id: number, data: UpdateAdmissionData) {
    return this.request.patch<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/admission`, data);
  }

  /**
   * Kurs nashr etilgandan keyin unga kiritilgan tahrirlar.
   *
   * Efirdagi kursning narxi yoki sarlavhasi o'zgartirilsa, bu moderatorlarga
   * ko'tariladi; aynan nima o'zgarganini shu iz ko'rsatadi.
   */
  getHistory(id: number) {
    return this.request.get<CourseChangeLog[], ApiResponse<CourseChangeLog[]>>(
      `${this.baseUrl}/${id}/history`,
    );
  }

  // ─── Chiqindi qutisi (admin) ───────────────────────────────────────────────

  /** Yumshoq o'chirilgan kurslar. O'chirish endi qatorni yo'q qilmaydi. */
  getDeleted(filter: CourseFilter) {
    return this.request.get<Course[], Pagination<Course>>(`${this.baseUrl}/admin/deleted`, {
      params: filter,
    });
  }

  /** Yumshoq o'chirilgan kursni qaytaradi; slug band bo'lsa, unga yangisi beriladi. */
  restore(id: number) {
    return this.request.post<Course, ApiResponse<Course>>(`${this.baseUrl}/${id}/restore`);
  }
}

export const coursesApi = new CoursesApi();
